/**
 * GT-RATE-LIMIT-ROOT-CAUSE-DRIVEN-FIX-07 — heavy-read backoff window
 * (client-side, RECORD-ONLY).
 *
 * Heavy reads from MANY independent React-Query consumers (Live Map polls,
 * Vehicles list, the always-on topbar, and the Trips per-vehicle route
 * fan-out) all share ONE `ui_read_heavy` per-IP bucket. When any one of
 * them trips the limiter, every OTHER heavy read fired in the same window
 * gets 429'd too — a sustained storm + a cascade of toasts.
 *
 * History: FIX-05 coordinated them with a client circuit breaker that
 * fail-fasted EVERY heavy GET while the window was open. The forensic
 * pass (FIX-06) proved that breaker was the proximate cause of the
 * owner-visible failures — a single 429 from one route blocked UNRELATED
 * user navigation (opening Vehicles / Trips) for up to 60 s. FIX-07
 * removed the fail-fast: a heavy 429 now only RECORDS the window. User
 * navigation always reaches the server. The window is relieved by pausing
 * BACKGROUND polling — heavy polling hooks read {@link heavyPollIntervalMs}
 * so their NEXT refetch lands after the limiter clears, never by blocking a
 * user-initiated request.
 *
 * The heavy prefixes mirror the server's route-class list
 * (`services/api/src/plugins/rate-limit-classes.ts`).
 */

const HEAVY_PREFIXES: readonly string[] = [
  "/v1/fleet",
  "/v1/vehicles",
  "/v1/devices",
  "/v1/telemetry",
  "/v1/trips",
  "/v1/geofences",
  "/v1/groups",
  "/v1/stop-events",
  "/v1/dashboard",
  "/v1/alerts",
];

/** Hard ceiling so a hostile Retry-After cannot freeze reads for minutes. */
export const MAX_BACKOFF_MS = 60_000;

let backoffUntil = 0;

/** True when `url` targets a heavy-read route (the shared `ui_read_heavy` bucket). */
export function isHeavyReadUrl(url: string): boolean {
  const path = url.split("?")[0] ?? url;
  return HEAVY_PREFIXES.some((p) => path.includes(p));
}

/**
 * Record a heavy-read 429 → open/extend the backoff window. `null`
 * Retry-After falls back to 30 s; clamped to {@link MAX_BACKOFF_MS}.
 */
export function noteHeavyReadRateLimited(
  retryAfterSeconds: number | null,
  now: number = Date.now(),
): void {
  const ms = Math.min((retryAfterSeconds ?? 30) * 1000, MAX_BACKOFF_MS);
  backoffUntil = Math.max(backoffUntil, now + ms);
}

/** Whole seconds remaining in the heavy-read backoff window (0 when clear). */
export function heavyReadBackoffRemainingSeconds(now: number = Date.now()): number {
  return Math.max(0, Math.ceil((backoffUntil - now) / 1000));
}

/**
 * GT-…-FIX-07 — `refetchInterval` for a HEAVY POLLING query.
 *
 * Returns `baseMs` normally, but while a heavy-read backoff window is open
 * it returns the remaining window (≥ baseMs) so the NEXT background poll
 * lands AFTER the limiter clears instead of re-hammering it. This is how
 * the shared bucket now recovers — by pausing BACKGROUND polling, NOT by
 * blocking user navigation (FIX-05's fail-fast was removed). Use as a
 * React Query `refetchInterval` FUNCTION so it is re-evaluated after each
 * fetch. `now` is injectable for tests.
 */
export function heavyPollIntervalMs(baseMs: number, now: number = Date.now()): number {
  const remaining = Math.max(0, backoffUntil - now);
  return remaining > 0 ? Math.max(remaining, baseMs) : baseMs;
}

/** Test-only reset. */
export function __resetApiBackoffForTests(): void {
  backoffUntil = 0;
}
